
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Target } from 'lucide-react';
import { useDivisionManagement } from '@/hooks/useDivisionManagement';
import { DivisionForm } from './DivisionForm';
import { DivisionTable } from './DivisionTable';

interface DivisionManagementProps {
  seasonId: string;
  seasonName?: string;
}

export const DivisionManagement = ({ seasonId, seasonName }: DivisionManagementProps) => {
  const {
    divisions,
    isLoading,
    isDialogOpen,
    setIsDialogOpen,
    editingDivision,
    divisionName,
    setDivisionName,
    travelLevel,
    setTravelLevel,
    birthDateCutoff,
    setBirthDateCutoff,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleDialogClose,
    isCreating,
    isUpdating,
  } = useDivisionManagement(seasonId);

  if (isLoading) {
    return <div>Loading divisions...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Divisions</h3>
          <p className="text-sm text-muted-foreground">
            Manage divisions within {seasonName || 'this season'}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Division
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDivision ? 'Edit Division' : 'Create New Division'}
              </DialogTitle>
              <DialogDescription>
                {editingDivision ? 'Update the division details.' : 'Enter the details for the new division.'}
              </DialogDescription>
            </DialogHeader>
            <DivisionForm
              editingDivision={editingDivision}
              divisionName={divisionName}
              setDivisionName={setDivisionName}
              travelLevel={travelLevel}
              setTravelLevel={setTravelLevel}
              birthDateCutoff={birthDateCutoff}
              setBirthDateCutoff={setBirthDateCutoff}
              isSubmitting={isCreating || isUpdating}
              onSubmit={handleSubmit}
              onCancel={handleDialogClose}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Divisions Table */}
      {divisions && divisions.length > 0 ? (
        <DivisionTable
          divisions={divisions}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Divisions Yet</h3>
            <p className="text-muted-foreground mb-4">
              This season doesn't have any divisions yet. Create the first division to organize teams.
            </p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Division
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
